import * as React from "react";
import styles from "./App.module.css"
import ProjectTags from "./ProjectTags";
import Footer from "./Footer";
import ProjectSearch from "./ProjectSearch";
import Project from "../types/Project";
import ProjectList from "./ProjectList";
import {PluginApi, PluginApiContext, ProjectsResponse} from "../services/PluginApi";
import {distinct, shallowEqualArray} from "../common/collection-utils";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import LoadingBar from "./LoadingBar";
import {appVersionString, isDev} from "../config";
import ItemId from "../types/ItemId";
import SyncFolder from "./SyncFolder";
import ProjectMailFolder from "../types/ProjectMailFolder";
import {CanaryUsers} from "../types/CanaryUsers";
import {UserId} from "../index";
import {GraphApiContext} from "../services/GraphApi";
import MailFolder from "../types/MailFolder";
import Outlook from "../services/Outlook";
import {useEffect} from "react";

/* global document, Office, module, require */

interface AppProps {
    officeContext: Office.Context,
    context?: any[]
}

const projectNumberRegex = new RegExp('TTW_(?<number>\\d{6})');

export const App = ({officeContext}: AppProps) => {
    const _ = officeContext;

    const pluginApi = React.useContext(PluginApiContext);
    const graphApi = React.useContext(GraphApiContext);
    const queryClient = useQueryClient();

    const [selectedItemIds, setSelectedItemIds] = React.useState<ItemId[]>([]);
    const [searchIsLoading, setSearchIsLoading] = React.useState<boolean>(false);
    const [mailFolder, setMailFolder] = React.useState<MailFolder>(undefined);
    const isCanaryUser = CanaryUsers.includes(UserId);

    const selectionCheckDelay = 250; //ms
    const selectedItemIdsRef = React.useRef<ItemId[]>([]);
    selectedItemIdsRef.current = selectedItemIds;

    let handlingSelection = false;
    const handlingSelectionRef = React.useRef(handlingSelection);

    // Searches the email's subject line for a string the matches 'TTW_######'. If the string is found
    // try to file the email to the project with that job number.
    const checkForJobNumberThenFile =  (
        e: Office.SelectedItemDetails, mutableRestId: string, internetMessageId: string) => {
        const result = projectNumberRegex.exec(e.subject);
        if (!result)
            return;

        const jobNumber = result.groups.number;
        fileEmailWithJobNumber.mutate({ jobNumber,
            mutableRestIds: [mutableRestId],
            internetMessageIds: [internetMessageId]
        });
    };

    // Create Filed Category if it doesn't exist
    React.useEffect(() => {
        Office.context.mailbox.masterCategories.getAsync((asyncResult) => {
            if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                console.log(`Could not get categories: ${asyncResult.error.message}`);
            } else {
                if (asyncResult.value.findIndex((cd => cd.displayName === 'Filed')) === -1) {
                    const filedCategory: Office.CategoryDetails[] = [
                        {
                            displayName: "Filed",
                            color: Office.MailboxEnums.CategoryColor.Preset5
                        }
                    ];
                    Office.context.mailbox.masterCategories.addAsync(filedCategory, (asyncResult) => {
                        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                            console.log("Failed to add the category")
                        }
                    });
                }
            }
        });
    }, []);

    // Selection handling
    React.useEffect(() => {
        (async () => {
            const handleSelectedItems = async (items: Office.SelectedItemDetails[]) => {
                if (handlingSelectionRef.current == true)
                    return;

                try {
                    handlingSelectionRef.current = true;

                    // Grab messages only
                    const selectedItemDetails = (items ?? [])
                        .filter(d => d.itemType.localeCompare(Office.MailboxEnums.ItemType.Message));

                    // Exchange Web Services Id
                    // This Id will cause failure on the MS Graph side on some messages
                    // Additionally, this Id will change locally if the user moves the email around
                    const ewsIds = selectedItemDetails.map(d => d.itemId);

                    // Internet Message Ids
                    let internetMessageIds = selectedItemDetails.map(d => d.internetMessageId);

                    // check if selection has actually changed
                    const alreadySelectedEwsIds = selectedItemIdsRef.current.map(i => i.ewsId);
                    const selectionChanged = !shallowEqualArray(alreadySelectedEwsIds, ewsIds);

                    if (selectionChanged) {
                        // Mutable REST id
                        // At minimum, this Id should be used on the MS Graph side, but it will change locally if the user
                        // moves the email around
                        const mutableRestIds = ewsIds
                            .map(id => Office.context.mailbox.convertToRestId(id, Office.MailboxEnums.RestVersion.v2_0));

                        // TODO: Remove this once bug is fixed in Outlook classic
                        internetMessageIds = await pluginApi.TEMP_EnsureWeHaveInternetMessageIdsMutableRestIds(mutableRestIds, internetMessageIds);

                        let itemIds: ItemId[] = [];
                        for (let i = 0; i < ewsIds.length; i++) {
                            itemIds.push({
                                ewsId: ewsIds[i],
                                mutableRestId: mutableRestIds[i],
                                internetMessageId: internetMessageIds[i],
                                /* immutableRestId: immutableRestIds[i]*/
                            });
                        }

                        // Automatically file email to project if there is a job number in the subject line.
                        if (selectedItemDetails.length === 1)
                            checkForJobNumberThenFile(selectedItemDetails[0], mutableRestIds[0], internetMessageIds[0]);

                        // Get folder if at least one item selected
                        if (selectedItemDetails.length > 0)
                            graphApi.getFolder(itemIds[0].mutableRestId)
                                .then(graphMailFolder => setMailFolder({
                                    id: graphMailFolder.id,
                                    name: graphMailFolder.displayName,
                                    wellKnownName: graphMailFolder.wellKnownName
                                }));

                        setSelectedItemIds(itemIds);
                    }
                } finally {
                    handlingSelectionRef.current = false;
                }
            };

            // Handle selection changes
            const selectedItemsChangedHandler = async (_: object) => {
                const selectedItems = await Outlook.getSelectedItems();
                await handleSelectedItems(selectedItems);
            }

            let doSelectionChecks = true;
            const regularSelectionCheck = async () => {
                if (!doSelectionChecks)
                    return;
                const selectedItems = await Outlook.getSelectedItems();
                await handleSelectedItems(selectedItems);
                setTimeout(() => regularSelectionCheck(), selectionCheckDelay);
            };
            await regularSelectionCheck();

            officeContext.mailbox.addHandlerAsync(Office.EventType.SelectedItemsChanged, selectedItemsChangedHandler)

            // Handle initial selection
            const selectedItems = await Outlook.getSelectedItems();
            await handleSelectedItems(selectedItems);
            return () => {
                doSelectionChecks = false;
                officeContext.mailbox.removeHandlerAsync(Office.EventType.SelectedItemsChanged, selectedItemsChangedHandler)
            };
        })();
    }, []);

    /* Projects Query */
    const emptyEmailProjects: ProjectsResponse =  { filed: [] };
    const qkProjectsKey = [...selectedItemIds.map(x => x.ewsId).sort()].join(';');
    const qkProjects = ["projects", qkProjectsKey];
    const {
        data: projectsResponse,
        isFetching: projectsIsLoading,
        isError: projectLoadingError
    }
    = useQuery<ProjectsResponse>({
        queryKey: qkProjects,
        queryFn: async () => {
            if (selectedItemIds) {
                const internetMessageIDs = selectedItemIds.map(i => i.internetMessageId);
                return await pluginApi.projects(internetMessageIDs);
            }             
            return emptyEmailProjects;
        },
        placeholderData: emptyEmailProjects,
    });

    /* Pinned Query */
    const qkPinned = ["pinned"];
    const {
        data: pinnedProjects,
        isFetching: pinnedProjectsIsLoading,
        isSuccess: pinnedProjectsSuccess
    } = useQuery<Project[]>({
        queryKey: qkPinned,
        queryFn: async () => await pluginApi.pinned(),
        placeholderData: []
    });

    /* Recents Query */
    const qkRecent = ["recent"];
    const {
        data: recentProjects,
        isFetching: recentProjectsIsLoading,
        isSuccess: recentProjectSuccess
    } = useQuery<Project[]>({
        queryKey: qkRecent,
        queryFn: async () => await pluginApi.recentUnpinned(50),
        placeholderData: []
    });

    /* Project Mail Folder Query */
    const emptyProjectMailFolder: ProjectMailFolder = {id: "", project: undefined, tracked: false};
    const qkProjectMailFolder = ['projectMailFolder', qkProjectsKey];
    const { data: projectMailFolder} = useQuery<ProjectMailFolder>({
       queryKey: qkProjectMailFolder,
       queryFn: async () => {
           if (selectedItemIds.length > 0)
               return await pluginApi.projectMailFolder(selectedItemIds[0].mutableRestId);
           return emptyProjectMailFolder;
       },
        placeholderData: emptyProjectMailFolder,
        initialData: emptyProjectMailFolder
    });

    // Set of projects to show
    const projects = distinct((projectsResponse ?? emptyEmailProjects).filed, p => p.id);

    useEffect(() => {
        (async () => {
            if (selectedItemIds.length > 0) {
                const item = await Outlook.loadItemById(selectedItemIds[0].ewsId);
                const p = await pluginApi.projects([selectedItemIds[0].internetMessageId])
                if (p.filed.length > 0)
                    await Outlook.addCategory(item as any, 'Filed');
                else
                    await Outlook.removeCategory(item as any, 'Filed');

                await Outlook.unloadItem(item);
            }
        })();
    }, [selectedItemIds]);

    /* File Mutation */
    const fileEmailToProject = useMutation({
        mutationFn: async (project: Project) => {
            const mutableRestIds = selectedItemIds.map(x => x.mutableRestId);
            const internetMessageIds = selectedItemIds.map(x => x.internetMessageId);
            return pluginApi.file(project.id, mutableRestIds, internetMessageIds);
        },

        // optimistic update
        onMutate: async (project) => {
            await queryClient.cancelQueries({queryKey: qkProjects});
            const previousProjects = queryClient.getQueryData<ProjectsResponse>(qkProjects);
            Outlook.addFiledCategoryToCurrentSelection();

            queryClient.setQueryData<ProjectsResponse>(
                qkProjects, (previous) => {
                    previous ??= emptyEmailProjects;
                    return {
                        filed: [...previous.filed, project],
                    };
                }
            );
            return {previousProjects};
        },

        // trigger query
        onSettled: () => {
            queryClient.invalidateQueries({queryKey: qkProjects});
            queryClient.invalidateQueries({queryKey: qkRecent});
        }
    });

    /* Unfile Mutation */
    const unfileEmailFromProject = useMutation({
        mutationFn: (projectId: string) => {
            const mutableRestIds = selectedItemIds.map(x => x.mutableRestId);
            const internetMessageIds = selectedItemIds.map(x => x.internetMessageId);
            return pluginApi.unfile(projectId, mutableRestIds, internetMessageIds);
        },

        // optimistic update
        onMutate: async (projectId) => {
            await queryClient.cancelQueries({queryKey: qkProjects});
            const previousProjects = queryClient.getQueryData<ProjectsResponse>(qkProjects);
            Outlook.removeFiledCategoryToCurrentSelection();

            queryClient.setQueryData<ProjectsResponse>(
                qkProjects, (previous) => {
                    previous ??= emptyEmailProjects;
                    return {
                        filed: previous.filed.filter(p => p.id !== projectId),
                    }
                }
            );
            return {previousProjects};
        },

        onSettled: async () => {
            queryClient.invalidateQueries({queryKey: qkProjects});
            queryClient.invalidateQueries({queryKey: qkRecent});
        }
    });

    const bulkFileToProject = useMutation({
        mutationFn: async (projectId: string) => {
            return await pluginApi.bulkFileEmailsInFolder(mailFolder.id, projectId);
        }
    });

    const fileEmailWithJobNumber = useMutation({
        mutationFn: (vars: { jobNumber: string, mutableRestIds: string[], internetMessageIds: string[] }) =>
            pluginApi.fileWithJobNumber(vars.jobNumber, vars.mutableRestIds, vars.internetMessageIds),

        onSettled: async () => {
            queryClient.invalidateQueries({ queryKey: qkProjects });
            queryClient.invalidateQueries({ queryKey: qkRecent });
        }
    });

    /* Pin project */
    const pinProject = useMutation({
        mutationFn: (project: Project) => {
            return pluginApi.pin(project.id);
        },

        onSettled: async () => {
            await queryClient.invalidateQueries({queryKey: qkRecent});
            await queryClient.invalidateQueries({queryKey: qkPinned});
        }
    });

    const unpinProject = useMutation({
        mutationFn: (project: Project) => {
            return pluginApi.unpin(project.id);
        },

        onSettled: async () => {
            await queryClient.invalidateQueries({queryKey: qkRecent});
            await queryClient.invalidateQueries({queryKey: qkPinned});
        }
    });

    /* Track Project Mail Folder Mutation */
    const trackProjectMailFolder = useMutation({
        mutationFn: (folderId: string) => pluginApi.trackMailFolder(folderId),

        // Optimistic update
        onMutate: async () => {
            await queryClient.cancelQueries({queryKey: qkProjectMailFolder});

            const projectMailFolder = queryClient.getQueryData<ProjectMailFolder>(qkProjectMailFolder);
            if (projectMailFolder.project == null)
                return projectMailFolder;

            return queryClient.setQueryData<ProjectMailFolder>(
                qkProjects, (previous) => {
                    previous.tracked = true;
                    return previous;
                }
            );
        },

        onSettled: async (success) => {
            if (success && projectMailFolder.id)
                pluginApi.fileEmailsInFolder(projectMailFolder.id).then();
            await queryClient.invalidateQueries({queryKey: qkProjectMailFolder});
        }
    });

    /* Track Project Mail Folder Mutation */
    const untrackProjectMailFolder = useMutation({
        mutationFn: (folderId: string) => pluginApi.untrackMailFolder(folderId),

        // Optimistic update
        onMutate: async () => {
            await queryClient.cancelQueries({queryKey: qkProjectMailFolder});

            const projectMailFolder = queryClient.getQueryData<ProjectMailFolder>(qkProjectMailFolder);
            if (projectMailFolder.project == null)
                return projectMailFolder;

            return queryClient.setQueryData<ProjectMailFolder>(
                qkProjects, (previous) => {
                    previous.tracked = false;
                    return previous;
                }
            );
        },

        onSettled: async () => {
            await queryClient.invalidateQueries({queryKey: qkProjectMailFolder});
        }
    });

    const isLoading = searchIsLoading || projectsIsLoading || pinnedProjectsIsLoading
        || recentProjectsIsLoading
        || fileEmailToProject.isPending || unfileEmailFromProject.isPending
        || trackProjectMailFolder.isPending || untrackProjectMailFolder.isPending;
    
    const devHeader = isDev && selectedItemIds.length > 0 
        ? (<p>{selectedItemIds.length} {selectedItemIds[0].internetMessageId}</p>) 
        : <></>;
    
    // Only allow filing to folder if its a user folder (e.g. do not allow filing of inbox)
    const allowFolderFiling = mailFolder && mailFolder.wellKnownName === null;
    const bulkFileFolder = allowFolderFiling
        ? bulkFileToProject.mutateAsync
        : undefined;

    return (
        <div className={styles.container}>
            <main className={styles.main}>
                {/*<Header/>*/}
                <div>
                    <LoadingBar enabled={isLoading} error={projectLoadingError}/>
                    { isCanaryUser
                        ? <SyncFolder projectMailFolder={projectMailFolder}
                                      trackProjectMailFolder={trackProjectMailFolder.mutate}
                                      untrackProjectMailFolder={untrackProjectMailFolder.mutate} />
                        : null
                    }
                    <ProjectSearch fileEmail={fileEmailToProject.mutate}
                                   pinProject={pinProject.mutate}
                                   isLoading={searchIsLoading}
                                   setIsLoading={setSearchIsLoading}
                                   selectCount={selectedItemIds.length}/>
                    <ProjectTags filedProjects={projects} unfileEmailsFromProject={unfileEmailFromProject.mutate}/>
                </div>
                <div className={styles.scrolling}>
                    <div>
                        {pinnedProjects.length > 0 &&
                            <ProjectList title={"Pinned"} projects={pinnedProjects} onSelect={fileEmailToProject.mutate}
                                         onPinClick={unpinProject.mutate} listKey={"pinned"}
                                         bulkFileFolder={bulkFileFolder}/>
                        }
                        <ProjectList title={"Recent"} projects={recentProjects} onSelect={fileEmailToProject.mutate}
                                     onPinClick={pinProject.mutate} listKey={"recents"}
                                     bulkFileFolder={bulkFileFolder}/>
                    </div>
                    <Footer version={appVersionString}/>
                </div>
            </main>
        </div>
    )
};